import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../layout/Layout';

interface LookingForItem {
  id: number;
  name: string;
  created_at: string;
}

const LookingFor: React.FC = () => {
  const [lookingFor, setLookingFor] = useState<LookingForItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    id: null as number | null,
    name: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchLookingFor();
  }, []);

  const fetchLookingFor = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/admin/app-assets/looking-for');
      if (response.data.success) {
        setLookingFor(response.data.data.looking_for);
      }
    } catch (error) {
      console.error('Error fetching looking for:', error);
    } finally {
      setLoading(false);
    }
  };

  const openModal = (item?: LookingForItem) => {
    if (item) {
      setFormData({ id: item.id, name: item.name });
    } else {
      setFormData({ id: null, name: '' });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setFormData({ id: null, name: '' });
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      alert('Please enter a name');
      return;
    }

    try {
      setSubmitting(true);
      if (formData.id) {
        await axios.put(`/admin/app-assets/looking-for/${formData.id}`, {
          name: formData.name
        });
        alert('Looking for option updated successfully!');
      } else {
        await axios.post('/admin/app-assets/looking-for', {
          name: formData.name
        });
        alert('Looking for option created successfully!');
      }
      closeModal();
      await fetchLookingFor();
    } catch (error: any) {
      console.error('Error saving looking for:', error);
      alert(error.response?.data?.message || 'Failed to save looking for option');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: number, name: string) => {
    if (!window.confirm(`Are you sure you want to delete "${name}"?`)) {
      return;
    }

    try {
      await axios.delete(`/admin/app-assets/looking-for/${id}`);
      alert('Looking for option deleted successfully!');
      await fetchLookingFor();
    } catch (error: any) {
      console.error('Error deleting looking for:', error);
      alert(error.response?.data?.message || 'Failed to delete looking for option');
    }
  };

  return (
    <Layout>
      <div>
        <div className="row">
          <div className="col-12">
            <div className="page-title-box d-sm-flex align-items-center justify-content-between">
              <div>
                <h4 className="mb-1 font-size-24 fw-semibold" style={{ color: '#495057' }}>Looking For</h4>
                <p className="text-muted mb-0">Manage user looking for preferences</p>
              </div>
              <div className="page-title-right">
                <button className="btn btn-primary" onClick={() => openModal()}>
                  <i className="bx bx-plus me-1"></i>
                  Add New
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-body">
                {loading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : lookingFor.length === 0 ? (
                  <div className="text-center py-5">
                    <i className="bx bx-search-alt font-size-48 text-muted mb-3 d-block"></i>
                    <p className="text-muted">No looking for options found</p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-hover">
                      <thead className="table-light">
                        <tr>
                          <th>ID</th>
                          <th>Name</th>
                          <th>Created Date</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {lookingFor.map(item => (
                          <tr key={item.id}>
                            <td>{item.id}</td>
                            <td className="fw-semibold">{item.name}</td>
                            <td>
                              <small>{new Date(item.created_at).toLocaleDateString()}</small>
                            </td>
                            <td>
                              <button
                                className="btn btn-sm btn-warning me-2"
                                onClick={() => openModal(item)}
                              >
                                <i className="bx bx-edit"></i>
                              </button>
                              <button
                                className="btn btn-sm btn-danger"
                                onClick={() => handleDelete(item.id, item.name)}
                              >
                                <i className="bx bx-trash"></i>
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Add/Edit Modal */}
        {showModal && (
          <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    {formData.id ? 'Edit Looking For' : 'Add New Looking For'}
                  </h5>
                  <button type="button" className="btn-close" onClick={closeModal}></button>
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Name *</label>
                    <input 
                      type="text" 
                      className="form-control"
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Enter looking for option"
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={closeModal}>
                    Cancel
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-primary"
                    onClick={handleSubmit}
                    disabled={submitting}
                  >
                    {submitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Saving...
                      </>
                    ) : (
                      <>
                        <i className="bx bx-save me-1"></i>
                        Save
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default LookingFor;





